import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from "../../@/components/ui/dialog";
import { Button } from "../../@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { api_endpoint } from "@/constants/constant";
import { useAuth } from "@clerk/clerk-react";
import { ClipLoader } from "react-spinners";

const getDeleteCategoryErrorMessage = (error, { hasChildren = false } = {}) => {
    if (hasChildren) {
        return "This category can’t be deleted because it has sub-categories. Delete/move the sub-categories first.";
    }

    const status = error?.response?.status;
    const data = error?.response?.data;

    const rawMessage =
        (typeof data === "string" && data) ||
        data?.message ||
        data?.error ||
        data?.detail ||
        error?.message;

    const normalized = String(rawMessage || "").toLowerCase();

    // Common cases: category has sub-categories, is referenced, or delete is blocked.
    const looksLikeHasChildren =
        normalized.includes("sub") ||
        normalized.includes("child") ||
        normalized.includes("children") ||
        normalized.includes("linked") ||
        normalized.includes("dependent") ||
        normalized.includes("constraint") ||
        normalized.includes("foreign key");

    // Backend sometimes returns 500 for constraint violations; treat that as dependency-related too.
    if (status === 409 || (status === 500 && looksLikeHasChildren) || looksLikeHasChildren) {
        return "This category can’t be deleted because it has sub-categories or is linked to other items. Delete/move the sub-categories first.";
    }

    if (status === 403) {
        return "You don’t have permission to delete this category.";
    }

    if (rawMessage) {
        return String(rawMessage);
    }

    return "Failed to delete category. Please try again.";
};

export default function DeleteCategoryDialog({ category, onDeleted, trigger, hasChildren = false }) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const { getToken } = useAuth();

    const handleDelete = async () => {
        if (hasChildren) {
            toast.error(
                "This category can’t be deleted because it has sub-categories. Delete/move the sub-categories first."
            );
            return;
        }

        setLoading(true);
        try {
            const token = await getToken();
            if (!token) return;
            const reqConfig = {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            };
            await axios.delete(api_endpoint + `/course/category/delete/${category.id}`, reqConfig);
            toast.success(`Category "${category.name}" deleted successfully!`);
            setOpen(false);
            if (onDeleted) onDeleted(category.id);
        } catch (error) {
            toast.error(getDeleteCategoryErrorMessage(error, { hasChildren }));
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {trigger ? (
                <span onClick={() => setOpen(true)}>{trigger}</span>
            ) : (
                <span
                    className="text-(--color-primary) font-medium hover:underline cursor-pointer"
                    onClick={() => setOpen(true)}
                >
                    Delete Category
                </span>
            )}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="h-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-(--neutral-900)">Delete Category</DialogTitle>
                    </DialogHeader>
                    <div className="font-body text-(--neutral-900)">
                        Are you sure you want to delete the category <b>{category.name}</b>? This action cannot be undone.
                    </div>
                    {hasChildren ? (
                        <div className="mt-3 rounded-md bg-(--neutral-100) p-3 text-sm text-(--neutral-900)">
                            This category has sub-categories. Delete/move the sub-categories first, then try again.
                        </div>
                    ) : null}
                    <DialogFooter className="">
                        <DialogClose asChild>
                            <Button
                                variant="outline"
                                onClick={() => setOpen(false)}
                                disabled={loading}
                                className=""
                            >
                                Cancel
                            </Button>
                        </DialogClose>
                        <Button
                            variant=""
                            onClick={handleDelete}
                            disabled={loading || hasChildren}
                            className=""
                        >
                            {loading ? (
                                <span className="flex items-center justify-center w-full">
                                    <ClipLoader size={20} color="#fff" />
                                </span>
                            ) : (
                                "Delete"
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}